import React, { useState, useEffect, useRef } from 'react';
import { 
  FunnelIcon, 
  ArrowsUpDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AcademicCapIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import useDocumentMetadata from '../hooks/useDocumentMetadata';
import SearchBar from '../components/search/SearchBar';
import ProgramCard from '../components/programs/ProgramCard';
import ProgramsFilterPanel from '../components/programs/ProgramsFilterPanel';
import DakhlaButterflyLoader from '../components/common/DakhlaButterflyLoader';
import { API_BASE_URL } from '../config/api';



const SORT_OPTIONS = [
  { label: 'Recommended', value: 'relevance' },
  { label: 'Tuition: Low to High', value: 'fee_asc' },
  { label: 'Tuition: High to Low', value: 'fee_desc' },
  { label: 'Program Name (A-Z)', value: 'name_asc' }
];

const Programs = () => {
  useDocumentMetadata(
    'Academic Programs in Pakistan 2026 - Fees, Admissions & Eligibility | Dakhla',
    'Explore and compare undergraduate, graduate, and doctorate programs across Pakistani universities. Filter by tuition fees, field of study, and city on Dakhla.'
  );

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const limit = 12;

  // Search & Filters State
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [filters, setFilters] = useState({
    degree_level: '',
    field_id: '',
    city: '',
    fee_min: '',
    fee_max: ''
  });
  const [sortBy, setSortBy] = useState('relevance');

  // Metadata dropdowns
  const [cities, setCities] = useState([]);
  const [fieldsOfStudy, setFieldsOfStudy] = useState([]);

  // Sidebar & mobile filter drawer state (matching Home.jsx)
  const [showFilters, setShowFilters] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1280;
    }
    return false;
  });

  // Handle mobile/desktop transition to prevent filter drawer from popping open on resize/zoom
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let wasDesktop = window.innerWidth >= 1280;

    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1280;
      if (wasDesktop && !isDesktop) {
        setShowFilters(false);
      } else if (!wasDesktop && isDesktop) {
        setShowFilters(true);
      }
      wasDesktop = isDesktop;
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const topAnchorRef = useRef(null);

  // Load cities and fields of study on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const [citiesRes, fieldsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/cities?has_programs=true`),
          fetch(`${API_BASE_URL}/fields-of-study`)
        ]);
        if (citiesRes.ok) {
          const citiesData = await citiesRes.json();
          setCities(Array.isArray(citiesData) ? citiesData : []);
        }
        if (fieldsRes.ok) {
          const fieldsData = await fieldsRes.json();
          setFieldsOfStudy(Array.isArray(fieldsData) ? fieldsData : []);
        }
      } catch (err) {
        console.error('Failed to load filter metadata:', err);
      }
    };

    fetchMetadata();
  }, []);

  // Fetch programs when filters, search, sort, or page changes
  useEffect(() => {
    let isCancelled = false;

    const fetchPrograms = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('page', page);
        params.append('limit', limit);
        if (activeSearch) params.append('search', activeSearch);
        if (filters.degree_level) params.append('degree_level', filters.degree_level);
        if (filters.field_id) params.append('field_id', filters.field_id);
        if (filters.city) params.append('city', filters.city);
        if (filters.fee_min && Number(filters.fee_min) > 0) params.append('fee_min', filters.fee_min);
        if (filters.fee_max && Number(filters.fee_max) < 500000) params.append('fee_max', filters.fee_max);
        if (sortBy) params.append('sort', sortBy);

        const res = await fetch(`${API_BASE_URL}/programs?${params.toString()}`);
        if (!res.ok) throw new Error(`HTTP error ${res.status}`);

        const data = await res.json();
        if (!isCancelled) {
          setPrograms(data.programs || []);
          setTotal(data.total || 0);
          setTotalPages(data.totalPages || 1);
        }
      } catch (err) {
        console.error('Error fetching programs:', err);
        if (!isCancelled) {
          setPrograms([]);
          setTotal(0);
          setTotalPages(1);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchPrograms();

    return () => {
      isCancelled = true;
    };
  }, [page, activeSearch, filters, sortBy]);

  // Scroll to top of list on page change
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === page) return;
    setPage(newPage);
    if (topAnchorRef.current) {
      topAnchorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleFilterChange = (key, value) => {
    setPage(1);
    if (key === 'fee_range_dual') {
      setFilters(prev => ({
        ...prev,
        fee_min: value.fee_min,
        fee_max: value.fee_max
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setActiveSearch('');
    setPage(1);
    setFilters({
      degree_level: '',
      field_id: '',
      city: '',
      fee_min: '',
      fee_max: ''
    });
    setSortBy('relevance');
  };

  const handleSearch = (newSearch, isSubmit) => {
    setSearchTerm(newSearch);
    if (isSubmit) {
      setPage(1);
      setActiveSearch(newSearch.trim());
      topAnchorRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Count active filters (excluding default degree_level if all)
  const activeFiltersCount = Object.entries(filters).filter(([key, val]) => {
    if (key === 'degree_level') return Boolean(val);
    if (key === 'fee_min') return Boolean(val) && Number(val) > 0;
    if (key === 'fee_max') return Boolean(val) && Number(val) < 500000;
    return val !== '' && val !== null && val !== undefined;
  }).length + (activeSearch ? 1 : 0);

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Hero Section */}
      <div className="relative bg-[#1E256F] text-white py-24 overflow-hidden shadow-sm">
        <div
          className="absolute top-0 left-0 w-full h-full pointer-events-none"
          style={{
            backgroundImage: "linear-gradient(to right, rgba(255, 255, 255, 0.07) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, 0.07) 1px, transparent 1px)",
            backgroundSize: "24px 24px"
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-3 whitespace-nowrap sm:whitespace-normal">
              Explore Academic Programs
            </h1>
            <p className="text-sm md:text-base lg:text-lg text-white/90 mb-6 font-normal max-w-3xl mx-auto md:whitespace-nowrap">
              Compare programs, fee structures, deadlines, and eligibility criteria — all in one place.
            </p>
            <div className="bg-white rounded-xl p-1.5 shadow-lg max-w-2xl mx-auto">
              <SearchBar
                onSearch={handleSearch}
                value={searchTerm}
                placeholder="Search programs, degrees, or universities"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8 w-full flex-1">
        <div className="flex flex-col xl:flex-row gap-6 items-start">
          {/* Filters Sidebar */}
          <div
            className={`w-full xl:w-1/4 xl:flex-shrink-0 xl:sticky xl:z-10 transition-all duration-300 ${
              showFilters ? 'hidden xl:block opacity-100' : 'hidden xl:hidden opacity-0'
            }`}
            style={{ top: 'calc(var(--navbar-height, 64px) + 24px)' }}
          >
            <ProgramsFilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onResetFilters={handleResetFilters}
              cities={cities}
              fieldsOfStudy={fieldsOfStudy}
              activeFiltersCount={activeFiltersCount}
            />
          </div>

          {/* Programs List Column */}
          <div
            className="w-full xl:w-auto xl:flex-1 xl:min-w-0 transition-all duration-300"
            ref={topAnchorRef}
            style={{ scrollMarginTop: 'calc(var(--navbar-height, 64px) + 24px)' }}
          >
            {/* Controls Toolbar */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-slate-800">
                  {total} {total === 1 ? 'Program' : 'Programs'} Found
                </h2>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {/* Toggle Filter Button */}
                <button
                  type="button"
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-xl text-sm font-semibold transition-all duration-300 ${
                    showFilters
                      ? 'bg-primary-50 border-primary-200 text-primary-700 hover:bg-primary-100'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm'
                  }`}
                >
                  <FunnelIcon className="h-4 w-4" />
                  <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                  {activeFiltersCount > 0 && !showFilters && (
                    <span className="h-4.5 min-w-[18px] px-1 rounded-full bg-primary-600 text-white text-[10px] font-bold flex items-center justify-center">
                      {activeFiltersCount}
                    </span>
                  )}
                </button>

                {/* Sort Dropdown */}
                <div className="relative flex items-center shadow-sm rounded-xl">
                  <ArrowsUpDownIcon className="h-4 w-4 absolute left-3 text-slate-500 pointer-events-none" />
                  <select
                    value={sortBy}
                    onChange={(e) => {
                      setPage(1);
                      setSortBy(e.target.value);
                    }}
                    aria-label="Sort programs by"
                    className="border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 rounded-xl pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-semibold transition-all cursor-pointer appearance-none"
                  >
                    {SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="h-4 w-4 absolute right-3 text-slate-500 pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Active Filters Pills */}
            {activeFiltersCount > 0 && (
              <div className="flex flex-wrap gap-2 items-center mb-4 animate-fade-in">
                {activeSearch && (
                  <span className="inline-flex items-center gap-1.5 bg-white text-slate-700 text-xs font-semibold pl-3 pr-2 py-1.5 rounded-full border border-slate-200 shadow-sm transition-all hover:border-slate-300">
                    <span>Search: "{activeSearch}"</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm('');
                        setActiveSearch('');
                        setPage(1);
                      }}
                      className="text-slate-400 hover:text-slate-600 font-bold ml-1 rounded-full p-0.5 hover:bg-slate-100 transition-colors"
                      aria-label="Clear search"
                    >
                      ✕
                    </button>
                  </span>
                )}
                {filters.degree_level && (
                  <span className="inline-flex items-center gap-1.5 bg-white text-slate-700 text-xs font-semibold pl-3 pr-2 py-1.5 rounded-full border border-slate-200 shadow-sm transition-all hover:border-slate-300">
                    <span>
                      Degree: {filters.degree_level === 'undergraduate' ? 'Undergraduate' : filters.degree_level === 'graduate' ? 'Graduate' : filters.degree_level === 'doctoral' ? 'Doctoral' : 'Associate'}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleFilterChange('degree_level', '')}
                      className="text-slate-400 hover:text-slate-600 font-bold ml-1 rounded-full p-0.5 hover:bg-slate-100 transition-colors"
                      aria-label="Remove degree level filter"
                    >
                      ✕
                    </button>
                  </span>
                )}
                {filters.city && (
                  <span className="inline-flex items-center gap-1.5 bg-white text-slate-700 text-xs font-semibold pl-3 pr-2 py-1.5 rounded-full border border-slate-200 shadow-sm transition-all hover:border-slate-300">
                    <span>City: {filters.city}</span>
                    <button
                      type="button"
                      onClick={() => handleFilterChange('city', '')}
                      className="text-slate-400 hover:text-slate-600 font-bold ml-1 rounded-full p-0.5 hover:bg-slate-100 transition-colors"
                      aria-label="Remove city filter"
                    >
                      ✕
                    </button>
                  </span>
                )}
                {filters.field_id && (
                  <span className="inline-flex items-center gap-1.5 bg-white text-slate-700 text-xs font-semibold pl-3 pr-2 py-1.5 rounded-full border border-slate-200 shadow-sm transition-all hover:border-slate-300">
                    <span>Field: {fieldsOfStudy.find(f => String(f.id) === String(filters.field_id))?.name || 'Selected'}</span>
                    <button
                      type="button"
                      onClick={() => handleFilterChange('field_id', '')}
                      className="text-slate-400 hover:text-slate-600 font-bold ml-1 rounded-full p-0.5 hover:bg-slate-100 transition-colors"
                      aria-label="Remove discipline filter"
                    >
                      ✕
                    </button>
                  </span>
                )}
                {(Boolean(filters.fee_min && Number(filters.fee_min) > 0) || Boolean(filters.fee_max && Number(filters.fee_max) < 500000)) && (
                  <span className="inline-flex items-center gap-1.5 bg-white text-slate-700 text-xs font-semibold pl-3 pr-2 py-1.5 rounded-full border border-slate-200 shadow-sm transition-all hover:border-slate-300">
                    <span>
                      Fee: {filters.fee_min && filters.fee_max ? `PKR ${Number(filters.fee_min).toLocaleString()} – ${Number(filters.fee_max).toLocaleString()}` : filters.fee_min ? `≥ PKR ${Number(filters.fee_min).toLocaleString()}` : `≤ PKR ${Number(filters.fee_max).toLocaleString()}`}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleFilterChange('fee_range_dual', { fee_min: '', fee_max: '' })}
                      className="text-slate-400 hover:text-slate-600 font-bold ml-1 rounded-full p-0.5 hover:bg-slate-100 transition-colors"
                      aria-label="Remove fee filter"
                    >
                      ✕
                    </button>
                  </span>
                )}
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="text-xs font-bold text-red-500 hover:text-red-700 transition-colors ml-1 px-2.5 py-1.5 hover:bg-red-50 rounded-lg"
                >
                  Clear All
                </button>
              </div>
            )}

            {/* Programs List */}
            {loading ? (
              <div className="py-20 flex flex-col items-center justify-center">
                <DakhlaButterflyLoader size="md" />
                <p className="mt-4 text-xs font-semibold text-slate-500">Searching degree programs...</p>
              </div>
            ) : programs.length > 0 ? (
              <>
                <div className="flex flex-col gap-4">
                  {programs.map((prog) => (
                    <ProgramCard key={prog.id} program={prog} />
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                    {/* Showing text */}
                    <div className="text-sm text-slate-500 font-medium">
                      Showing <span className="font-semibold text-slate-900">{(page - 1) * limit + 1}</span> -{" "}
                      <span className="font-semibold text-slate-900">{Math.min(page * limit, total)}</span> of{" "}
                      <span className="font-semibold text-slate-900">{total}</span> results
                    </div>

                    {/* Page buttons */}
                    <div className="flex items-center space-x-1.5">
                      <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        className="p-2 border border-slate-300 rounded-lg text-slate-600 bg-white hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                        aria-label="Previous page"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>

                      {/* Page Numbers */}
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNum = index + 1;
                        if (
                          pageNum === 1 ||
                          pageNum === totalPages ||
                          (pageNum >= page - 1 && pageNum <= page + 1)
                        ) {
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`px-3 py-1.5 border rounded-lg text-sm font-semibold transition-all ${
                                page === pageNum
                                  ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 shadow-sm'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (
                          (pageNum === page - 2 && page > 3) ||
                          (pageNum === page + 2 && page < totalPages - 2)
                        ) {
                          return <span key={pageNum} className="px-2 text-slate-400">...</span>;
                        }
                        return null;
                      })}

                      <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                        className="p-2 border border-slate-300 rounded-lg text-slate-600 bg-white hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-sm"
                        aria-label="Next page"
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Empty Results State */
              <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-xs">
                <div className="mx-auto w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 mb-4">
                  <AcademicCapIcon className="h-6 w-6" />
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-1">No matching degree programs found</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto mb-6">
                  We couldn't find any programs matching your current filter criteria. Try clearing some filters or searching with a different term.
                </p>
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-xs font-bold rounded-xl transition-colors shadow-sm"
                >
                  <span>Reset All Filters</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      {showFilters && (
        <div className="fixed inset-0 z-50 xl:hidden">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-slate-900/50 transition-opacity duration-300"
            onClick={() => setShowFilters(false)}
          />
          
          {/* Drawer Content */}
          <div className="fixed inset-y-0 left-0 w-full max-w-xs sm:max-w-sm bg-white shadow-2xl p-6 flex flex-col h-full animate-slide-in-left overflow-hidden">
            <div className="flex items-center justify-between mb-6 border-b border-slate-100 pb-4">
              <div className="flex items-center gap-2">
                <FunnelIcon className="h-5 w-5 text-slate-700" />
                <h3 className="text-lg font-bold text-slate-800">Filters</h3>
              </div>
              <button 
                type="button"
                onClick={() => setShowFilters(false)}
                className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
                aria-label="Close filters menu"
              >
                <span className="text-xl font-bold">✕</span>
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto scrollbar-hide pr-1">
              <ProgramsFilterPanel 
                filters={filters} 
                onFilterChange={handleFilterChange} 
                onResetFilters={handleResetFilters}
                cities={cities}
                fieldsOfStudy={fieldsOfStudy}
                activeFiltersCount={activeFiltersCount}
                isMobileDrawer={true}
                onCloseDrawer={() => setShowFilters(false)}
              />
            </div>
            
            {/* View Results Button at Bottom for Mobile */}
            <div className="border-t border-slate-100 pt-4 mt-6">
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                className="w-full py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold text-sm rounded-xl transition-colors shadow-sm flex justify-center items-center"
              >
                Show {total} {total === 1 ? 'Result' : 'Results'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Programs;
